<template>
  <div
    v-if="showInstructions"
    class="mb-4 flex items-center bg-blue-50 border-l-4 border-blue-400 p-4 rounded"
  >
    <span v-html="INFO_ICON" class="w-6 h-6 text-blue-400 flex-shrink-0"></span>
    <span class="ml-3 text-base text-gray-700 leading-normal">
      To invite expert via a custom link you can copy and paste this link
      anywhere. For example you may share this on social media, in a Slack/Team
      chat, or with your Account Manager or CSM colleagues to share with their
      customer contacts. You have the option to decide if you want the signup
      link to go to your customized program landing page first or directly to
      the profile signup form. After you send the invites you can check the
      expert status in the Experts tab.
    </span>
  </div>
  <div
    v-if="invitationType === 'experts'"
    class="flex flex-col gap-2 w-full bg-white rounded-lg p-4 shadow"
  >
    <div class="flex items-center gap-2">
      <span class="text-sm font-medium">Invite via Link</span>
      <button
        class="flex items-center gap-2 bg-gray-100 text-primary-purple px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary-purple hover:text-white transition"
        :disabled="!inviteLink"
        @click="copyLink"
      >
        <span v-html="NOTE_ICON" class="w-5 h-5"></span>
        <span>{{ copied ? "Copied!" : "Copy Link" }}</span>
      </button>
      <span v-if="copied" class="text-primary-purple text-xs ml-2"
        >Link Copied!</span
      >
    </div>
    <div class="text-xs text-gray-500 break-all">{{ inviteLink }}</div>
    <div v-if="errorMessage" class="text-xs text-red-500 mt-1">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { gql, ApolloError } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { useUserStore } from "@/stores/user";
import { NOTE_ICON } from "@/assets/svg/shared/svg-constants";
import { INFO_ICON } from "@/assets/svg/shared/svg-constants";

const GENERATE_LINK_MUTATION = gql`
  mutation generateInviteLink($input: GenerateInviteLinkInput!) {
    generateInviteLink(input: $input) {
      invitation {
        inviteUrl
      }
      errors
    }
  }
`;

export default defineComponent({
  name: "InviteByLinkSection",
  props: {
    invitationType: {
      type: String,
      required: true,
    },
    directSignup: {
      type: Boolean,
      required: false,
      default: false,
    },
    showInstructions: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const baseInviteUrl = ref("");
    const copied = ref(false);
    const errorMessage = ref("");
    const userStore = useUserStore();

    const {
      mutate: generateLink,
      onDone,
      onError,
    } = useMutation(GENERATE_LINK_MUTATION);

    const callGenerateLink = () => {
      generateLink({
        input: {
          inviteParams: {
            programId:
              userStore.createExpertProgram?.id ||
              userStore.selectedProgram?.id,
          },
        },
      });
    };

    onDone((response) => {
      if (response && response?.data?.generateInviteLink) {
        const data = response.data.generateInviteLink;
        if (data.errors.length > 0) {
          errorMessage.value = data.errors.join(". ");
        } else {
          baseInviteUrl.value = data.invitation.inviteUrl;
        }
      } else {
        errorMessage.value = "Failed to generate invite link.";
      }
    });
    onError((error: ApolloError) => {
      errorMessage.value = error.message;
    });

    onMounted(() => {
      if (props.invitationType === "experts") callGenerateLink();
    });

    const inviteLink = computed(() => {
      if (!baseInviteUrl.value) return "";
      let url = baseInviteUrl.value;
      const programUuid =
        userStore.createExpertProgram?.uuid ||
        userStore.selectedProgram?.uuid ||
        "";
      const params = [];
      if (programUuid)
        params.push(`programUuid=${encodeURIComponent(programUuid)}`);
      if (props.directSignup) params.push("directSignup=true");
      if (params.length) {
        url += (url.includes("?") ? "&" : "?") + params.join("&");
      }
      return url;
    });

    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(inviteLink.value);
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);
      } catch (e) {
        errorMessage.value = "Failed to copy link.";
      }
    };

    return { inviteLink, copied, copyLink, errorMessage, NOTE_ICON, INFO_ICON };
  },
});
</script>
